// El plazo regulatorio de un caso como lo dice la fila de la bandeja (HU-34-C3, `ui/MG-ILE-67.md`):
// arriba el texto corto en seminegrita con su ícono («Vencido hace 2 días»), debajo el detalle
// («Vencía 05/10/2026») y, si la fecha se calculó sin el calendario del país, el aviso.
//
// Vivía dentro de `Bandeja.tsx` (`PlazoDelCaso`). HU-34b (`ui/MG-ILE-1092.md`, V1) pide que la
// alerta de plazos del Dashboard diga EL MISMO texto que la fila: se saca aquí para que las dos
// pantallas lo pinten con el mismo código y no con dos copias (§8.1 R8). Cada dato lo da el servidor;
// con un servidor anterior, sin texto corto, se pinta el texto del semáforo en su insignia.

import type { ReactNode } from 'react';
import { StatusBadge } from './ui';
import { TEXTO_DEL_PLAZO_NO_FIABLE, iconoDelServidor, tonoDelSemaforo } from '../lib/etiquetas';

/** Lo que hace falta del contrato para pintar el plazo; lo arma cada pantalla desde su fila. */
export type DatosDelPlazo = {
  /** `semaforo.estado`: `vencido`, `por_vencer`, `en_plazo`, `cumplido`, `sin_plazo_*`. */
  estado: string;
  /** El ícono semántico que nombra el servidor (`semaforo.icono`). */
  icono: string;
  /** El texto largo del semáforo («Vencido · notificación»): el respaldo sin texto corto. */
  textoDelSemaforo: string;
  textoCorto?: string | null;
  detalle?: string | null;
  fiable: boolean;
};

/** El color del texto corto del plazo según el semáforo. Lo neutro (cumplido, sin plazo) va en tinta. */
const COLOR_DEL_PLAZO: Record<string, string> = {
  vencido: 'text-rose-700',
  por_vencer: 'text-amber-700',
  en_plazo: 'text-emerald-700',
};

/** Los semáforos sin fecha que vigilar: el plazo es un chip neutro y el detalle, su ayuda. */
const SIN_PLAZO = new Set(['sin_plazo_configurado', 'sin_plazo_regulatorio']);

/**
 * El plazo con su ícono y su texto, nunca solo el color. `children` va al pie (la bandeja pone ahí
 * los destinos de un caso con varias cartas).
 */
export default function TextoDelPlazo({ plazo, children }: Readonly<{ plazo: DatosDelPlazo; children?: ReactNode }>) {
  const Icono = iconoDelServidor(plazo.icono);
  const corto = plazo.textoCorto || plazo.textoDelSemaforo;
  const detalle = plazo.detalle ?? null;
  const aviso = !plazo.fiable && <span className="text-meta text-amber-700">{TEXTO_DEL_PLAZO_NO_FIABLE}</span>;
  if (SIN_PLAZO.has(plazo.estado) || !plazo.textoCorto) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5" title={detalle ?? undefined}>
        <StatusBadge tone={tonoDelSemaforo(plazo.estado)} icon={Icono}>{corto}</StatusBadge>
        {detalle && <span className="sr-only">{detalle}</span>}
        {aviso}
        {children}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <b className={'inline-flex items-center gap-1 font-semibold whitespace-nowrap ' + (COLOR_DEL_PLAZO[plazo.estado] ?? 'text-ink')}>
        <Icono size={13} aria-hidden="true" className="shrink-0" />
        {corto}
      </b>
      {detalle && <span className="text-meta text-ink-soft">{detalle}</span>}
      {aviso}
      {children}
    </span>
  );
}
